import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  TextField,
  Button,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  useMediaQuery,
  Alert,
  Chip,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  InputAdornment,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Divider,
  Rating,
  CircularProgress,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  CardHeader,
  Badge,
  Slider,
  Tooltip,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  Delete,
  CheckCircle,
  Search,
  Edit,
  Visibility,
  Download,
  Refresh,
  PersonAdd,
  Person,
  Hotel,
  Room,
  CalendarToday,
  CreditCard,
  Close,
  History,
  Phone,
  Email,
  LocationOn,
  Percent,
  AddCircle,
  RemoveCircle,
  Info,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import Sidebar from '../../../../components/sidebars/sidebar_reception';
import TopNav from '../../../../components/TopNav_hotelStaff';
import StatisticsCards from '../../../../components/StatisticsCard';
import axios from 'axios';

// Styled components
const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const FileUploadBox = styled(Paper)(({ theme }) => ({
  border: `2px dashed ${theme.palette.primary.main}`,
  borderRadius: 2,
  padding: theme.spacing(3),
  textAlign: 'center',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    borderColor: theme.palette.primary.dark,
  },
}));

const StyledTabs = styled(Tabs)(({ theme }) => ({
  '& .MuiTabs-indicator': {
    backgroundColor: theme.palette.primary.main,
    height: 3,
    borderRadius: '3px 3px 0 0',
  },
}));

const StyledTab = styled(Tab)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '14px',
  minWidth: 120,
  color: theme.palette.text.secondary,
  '&.Mui-selected': {
    color: theme.palette.primary.main,
  },
  '&:hover': {
    color: theme.palette.primary.main,
    backgroundColor: 'transparent',
  },
}));

const StatusChip = styled(Chip)(({ theme, status }) => {
  const getColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'confirmed':
      case 'paid':
        return theme.palette.success.main;
      case 'pending':
        return theme.palette.warning.main;
      case 'cancelled':
        return theme.palette.error.main;
      case 'checked_in':
        return theme.palette.info.main;
      case 'checked_out':
        return theme.palette.grey[500];
      default:
        return theme.palette.grey[500];
    }
  };

  return {
    backgroundColor: getColor(status),
    color: theme.palette.common.white,
    fontWeight: 'bold',
  };
});

const ReviewCard = styled(Card)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.action.hover} 100%)`,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '12px',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[8],
  },
}));

const RoomCard = styled(Card)(({ theme }) => ({
  border: `2px solid ${theme.palette.divider}`,
  borderRadius: '12px',
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[8],
    borderColor: theme.palette.primary.main,
  },
  '&.selected': {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.action.selected,
  },
}));

const SectionCard = styled(Card)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '12px',
  marginBottom: theme.spacing(3),
  overflow: 'visible',
}));

const SectionHeader = styled(CardHeader)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: 'white',
  borderTopLeftRadius: '12px',
  borderTopRightRadius: '12px',
  '& .MuiCardHeader-title': {
    fontSize: '1.1rem',
    fontWeight: 600,
  },
}));

const DiscountSlider = styled(Slider)(({ theme }) => ({
  color: theme.palette.success.main,
  '& .MuiSlider-thumb': {
    backgroundColor: theme.palette.success.main,
    '&:hover, &.Mui-focusVisible': {
      boxShadow: `0px 0px 0px 8px ${theme.palette.success.main}33`,
    },
  },
  '& .MuiSlider-track': {
    backgroundColor: theme.palette.success.main,
  },
  '& .MuiSlider-rail': {
    backgroundColor: theme.palette.grey[300],
  },
}));

// API base URL
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const HOTEL_NAME = process.env.REACT_APP_HOTEL_NAME;

const HotelGuestBooking = () => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width:768px)');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [activeStep, setActiveStep] = useState(0);
  const [loadingRooms, setLoadingRooms] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  const [filters, setFilters] = useState({
    status: '',
    roomType: '',
    dateRange: '',
  });
  
  // State management
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [availableRooms, setAvailableRooms] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Discount state
  const [activeDiscount, setActiveDiscount] = useState(null);
  const [discountLoading, setDiscountLoading] = useState(false);
  
  // NEW: Manual discount state for receptionist to apply during booking
  const [manualDiscountPercentage, setManualDiscountPercentage] = useState(0);
  const [manualDiscountReason, setManualDiscountReason] = useState('');
  const [showManualDiscount, setShowManualDiscount] = useState(false);

  // New states for guest auto-population
  const [searchingGuest, setSearchingGuest] = useState(false);
  const [foundGuests, setFoundGuests] = useState([]);
  const [guestSearchDialog, setGuestSearchDialog] = useState(false);
  const [selectedExistingGuest, setSelectedExistingGuest] = useState(null);

  // View booking dialog state
  const [viewBookingDialog, setViewBookingDialog] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);

  // Get hotel code and staff code from user context or localStorage
  const getHotelCode = () => {
    const storedProfile = JSON.parse(localStorage.getItem('userProfile')) || {};
    const hotelCode = storedProfile.hotelcode || storedProfile.hotel_code || storedProfile.hotelCode || '';
    
    if (!hotelCode) {
      console.warn('No hotel code found in user profile, using fallback');
      return 'HOTEL001';
    }
    
    return hotelCode;
  };

  const getStaffCode = () => {
    const storedProfile = JSON.parse(localStorage.getItem('userProfile')) || {};
    const staffCode = storedProfile.staff_id || storedProfile.staff_code || storedProfile.staffCode || '';
    
    if (!staffCode) {
      console.warn('No staff code found in user profile');
      return null;
    }
    
    return staffCode;
  };

  const getStaffName = () => {
    const storedProfile = JSON.parse(localStorage.getItem('userProfile')) || {};
    const firstName = storedProfile.first_name || storedProfile.firstName || '';
    const lastName = storedProfile.last_name || storedProfile.lastName || '';
    const fullName = `${firstName} ${lastName}`.trim();
    return fullName || 'Staff User';
  };

  const [formData, setFormData] = useState({
    // Guest Information
    title: '',
    firstName: '',
    lastName: '',
    middleName: '',
    gender: '',
    nationality: '',
    
    // Contact Information (moved to top for auto-population)
    email: '',
    phone: '',
    address: '',
    country: '',
    
    // Emergency Contact
    emergencyContactName: '',
    emergencyContactPhone: '',
    
    // Preferences
    preferences: '',
    
    // Booking Information
    checkInDate: '',
    checkOutDate: '',
    numberOfGuests: 1,
    numberOfRooms: 1,
    specialRequests: '',
    
    // Payment Information
    paymentMethod: '',
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    billingAddress: '',
  });

  const [uploadedFiles, setUploadedFiles] = useState({
    passportPhoto: null,
    idDocument: null,
    paymentReceipt: null,
  });

  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);

  // Format date to YYYY-MM-DD for API
  const formatDateForAPI = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  };

  // FIXED: Check for active discount with correct endpoint
  const checkActiveDiscount = async () => {
    const hotelCode = getHotelCode();
    if (!hotelCode) {
      console.log('No hotel code available for discount check');
      return;
    }

    setDiscountLoading(true);
    console.log('Checking for active discount for hotel:', hotelCode);
    
    try {
      // Use the correct API endpoint for discount check
      const response = await axios.get(`${API_BASE_URL}/hotel/rooms/discount_check.php`, {
        params: { hotel_code: hotelCode }
      });
      
      console.log('Discount check response:', response.data);
      
      if (response.data.success && response.data.discount) {
        console.log('Active discount found:', response.data.discount);
        setActiveDiscount(response.data.discount);
        showSnackbar(`${response.data.discount.discount_percentage}% discount is active!`, 'info');
      } else {
        console.log('No active discount found');
        setActiveDiscount(null);
      }
    } catch (error) {
      console.error('Error checking discount:', error);
      console.error('Error details:', error.response?.data);
      setActiveDiscount(null);
    } finally {
      setDiscountLoading(false);
    }
  };

  // Calculate nights between dates
  const calculateNights = () => {
    if (!formData.checkInDate || !formData.checkOutDate) return 0;
    const checkIn = new Date(formData.checkInDate);
    const checkOut = new Date(formData.checkOutDate);
    return Math.max(0, Math.ceil((checkOut - checkIn) / (1000 * 60 * 60 * 24)));
  };

  // Calculate original total without discount
  const calculateOriginalTotal = () => {
    if (!selectedRoom) return 0;
    const nights = calculateNights();
    return selectedRoom.price * nights;
  };

  // Get the effective discount percentage (higher of global or manual)
  const getEffectiveDiscountPercentage = () => {
    const globalDiscount = activeDiscount ? parseFloat(activeDiscount.discount_percentage) : 0;
    const manualDiscount = parseFloat(manualDiscountPercentage) || 0;
    // Use manual discount if set, otherwise use global discount
    const effective = manualDiscount > 0 ? manualDiscount : globalDiscount;
    console.log('Effective discount calculation:', { globalDiscount, manualDiscount, effective });
    return effective;
  };

  // Calculate discount amount
  const calculateDiscountAmount = () => {
    const originalTotal = calculateOriginalTotal();
    const discountPercent = getEffectiveDiscountPercentage();
    if (originalTotal <= 0 || discountPercent <= 0) return 0;
    return (originalTotal * discountPercent) / 100;
  };

  // Calculate final total with discount
  const calculateTotal = () => {
    const originalTotal = calculateOriginalTotal();
    const discountAmount = calculateDiscountAmount();
    return originalTotal - discountAmount;
  };

  // Get discount percentage for display
  const getDisplayDiscountPercentage = () => {
    return getEffectiveDiscountPercentage();
  };

  // Check if discount is manually applied
  const isManualDiscountApplied = () => {
    return parseFloat(manualDiscountPercentage) > 0;
  };

  // Get discount source for display
  const getDiscountSource = () => {
    if (isManualDiscountApplied()) {
      return 'Manual';
    } else if (activeDiscount) {
      return 'Global';
    }
    return 'None';
  };

  // Handle manual discount change
  const handleManualDiscountChange = (event) => {
    const value = parseFloat(event.target.value) || 0;
    console.log('Manual discount changed to:', value);
    // Clamp value between 0 and 100
    const clampedValue = Math.min(100, Math.max(0, value));
    setManualDiscountPercentage(clampedValue);
    
    // If manual discount is set, show the reason field
    if (clampedValue > 0) {
      setShowManualDiscount(true);
    } else {
      setShowManualDiscount(false);
      setManualDiscountReason('');
    }
  };

  // Handle manual discount reason change
  const handleManualDiscountReasonChange = (event) => {
    setManualDiscountReason(event.target.value);
  };

  // Toggle manual discount
  const toggleManualDiscount = () => {
    if (showManualDiscount) {
      // Reset manual discount
      setManualDiscountPercentage(0);
      setManualDiscountReason('');
      setShowManualDiscount(false);
      showSnackbar('Manual discount removed', 'info');
    } else {
      setShowManualDiscount(true);
    }
  };

  // API service functions
  const api = {
    // Create booking with staff code and discount
    createBooking: async (bookingData) => {
      const staffCode = getStaffCode();
      const staffName = getStaffName();
      
      const dataWithStaff = {
        ...bookingData,
        staff_id: staffCode,
        staff_name: staffName,
      };
      
      console.log('=== API createBooking Debug ===');
      console.log('Discount data being sent:', {
        discount_percentage: dataWithStaff.discount_percentage,
        discount_amount: dataWithStaff.discount_amount,
        original_amount: dataWithStaff.original_amount,
        total_amount: dataWithStaff.total_amount,
        discount_id: dataWithStaff.discount_id,
        discount_reason: dataWithStaff.discount_reason,
        discount_approved_by: dataWithStaff.discount_approved_by,
        is_manual_discount: dataWithStaff.is_manual_discount
      });
      console.log('Full booking data:', dataWithStaff);
      
      const response = await axios.post(`${API_BASE_URL}/hotel/staff/reception/create.php`, dataWithStaff, {
        headers: {
          'Content-Type': 'application/json',
          'X-Staff-ID': staffCode
        }
      });
      return response.data;
    },

    // Get all bookings
    getBookings: async (params = {}) => {
      const response = await axios.get(`${API_BASE_URL}/hotel/staff/reception/index.php`, {
        params: { hotel_code: getHotelCode(), ...params }
      });
      return response.data;
    },

    // Get single booking
    getBooking: async (id) => {
      const response = await axios.get(`${API_BASE_URL}/hotel/staff/reception/get.php`, {
        params: { id, hotel_code: getHotelCode() }
      });
      return response.data;
    },

    // Update booking status with staff code for cancellations
    updateBookingStatus: async (id, status, cancellationReason = null) => {
      const staffCode = getStaffCode();
      const requestData = {
        id,
        status,
        hotel_code: getHotelCode(),
        cancellation_reason: cancellationReason
      };
      
      if (status === 'cancelled' && staffCode) {
        requestData.cancelled_by = staffCode;
      }
      
      const response = await axios.put(`${API_BASE_URL}/hotel/staff/reception/status.php`, requestData);
      return response.data;
    },

    // Get room availability
    getRoomAvailability: async (checkIn, checkOut) => {
      const formattedCheckIn = formatDateForAPI(checkIn);
      const formattedCheckOut = formatDateForAPI(checkOut);
      
      console.log('Checking availability for:', {
        hotel_code: getHotelCode(),
        check_in: formattedCheckIn,
        check_out: formattedCheckOut
      });
      
      const response = await axios.get(`${API_BASE_URL}/hotel/staff/reception/availability.php`, {
        params: {
          hotel_code: getHotelCode(),
          check_in: formattedCheckIn,
          check_out: formattedCheckOut
        }
      });
      return response.data;
    },

    // Get booking statistics
    getStatistics: async () => {
      const response = await axios.get(`${API_BASE_URL}/hotel/staff/reception/statistics.php`, {
        params: { hotel_code: getHotelCode() }
      });
      return response.data;
    },

    // Search for existing guests
    searchGuests: async (searchTerm) => {
      try {
        const response = await axios.get(`${API_BASE_URL}/hotel/staff/reception/guests.php`, {
          params: { 
            hotel_code: getHotelCode(),
            search: searchTerm,
            limit: 10
          }
        });
        console.log('Guest search response:', response.data);
        return response.data;
      } catch (error) {
        console.error('Error searching guests:', error);
        return { success: false, error: 'Failed to search guests' };
      }
    },

    // Get guest by ID
    getGuest: async (id) => {
      try {
        const response = await axios.get(`${API_BASE_URL}/hotel/staff/reception/guest.php`, {
          params: { 
            hotel_code: getHotelCode(),
            id: id
          }
        });
        return response.data;
      } catch (error) {
        console.error('Error getting guest:', error);
        return { success: false, error: 'Failed to get guest details' };
      }
    }
  };

  // Check discount when component mounts and when hotel code changes
  useEffect(() => {
    console.log('Component mounted, checking active discount...');
    checkActiveDiscount();
  }, []);

  // Debounced guest search
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if ((formData.email && formData.email.length > 3) || (formData.phone && formData.phone.length > 3)) {
        searchExistingGuests();
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [formData.email, formData.phone]);

  // Search for existing guests
  const searchExistingGuests = async () => {
    const searchTerm = formData.email || formData.phone;
    if (!searchTerm || searchTerm.length < 4) return;

    setSearchingGuest(true);
    console.log('Searching for guests with:', searchTerm);
    
    try {
      const result = await api.searchGuests(searchTerm);
      console.log('Search result:', result);
      
      if (result.success && result.guests && result.guests.length > 0) {
        setFoundGuests(result.guests);
        if (result.guests.length === 1) {
          // Auto-select if only one guest found
          handleSelectGuest(result.guests[0]);
        } else {
          // Show dialog if multiple guests found
          setGuestSearchDialog(true);
        }
      } else {
        setFoundGuests([]);
        // Clear any previously selected guest if not found
        if (selectedExistingGuest) {
          setSelectedExistingGuest(null);
        }
      }
    } catch (error) {
      console.error('Error searching guests:', error);
      setFoundGuests([]);
    } finally {
      setSearchingGuest(false);
    }
  };

  // Handle guest selection
  const handleSelectGuest = (guest) => {
    console.log('Selecting guest:', guest);
    setFormData(prev => ({
      ...prev,
      title: guest.title || '',
      firstName: guest.first_name || '',
      lastName: guest.last_name || '',
      middleName: guest.middle_name || '',
      gender: guest.gender || '',
      nationality: guest.nationality || '',
      email: guest.email || '',       
      phone: guest.phone || '',        
      address: guest.address || '',
      country: guest.country || '',
      emergencyContactName: guest.emergency_contact_name || '',
      emergencyContactPhone: guest.emergency_contact_phone || '',
      preferences: guest.preferences || ''
    }));
    setSelectedExistingGuest(guest);
    setGuestSearchDialog(false);
    showSnackbar(`Guest information loaded for ${guest.first_name} ${guest.last_name}`, 'success');
  };

  // Handle manual guest selection from dialog
  const handleManualGuestSelect = (guest) => {
    handleSelectGuest(guest);
  };

  // Clear guest selection
  const handleClearGuestSelection = () => {
    setSelectedExistingGuest(null);
    setFoundGuests([]);
    setFormData(prev => ({
      ...prev,
      title: '',
      firstName: '',
      lastName: '',
      middleName: '',
      gender: '',
      nationality: '',
      address: '',
      country: '',
      emergencyContactName: '',
      emergencyContactPhone: '',
      preferences: ''
    }));
    showSnackbar('Guest selection cleared', 'info');
  };

  // Handle view booking
  const handleViewBooking = async (booking) => {
    try {
      setLoading(true);
      console.log('Viewing booking:', booking.id);
      const result = await api.getBooking(booking.id);
      console.log('Booking details:', result);
      
      if (result.success) {
        setSelectedBooking(result.booking);
        setViewBookingDialog(true);
      } else {
        showSnackbar('Failed to load booking details', 'error');
      }
    } catch (error) {
      console.error('Error viewing booking:', error);
      showSnackbar('Error loading booking details', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Load initial data
  useEffect(() => {
    if (activeTab === 1) {
      loadBookings();
    }
  }, [activeTab]);

  // Load bookings when filters change
  useEffect(() => {
    if (activeTab === 1) {
      loadBookings();
    }
  }, [filters, searchQuery]);

  // Load available rooms when dates change
  useEffect(() => {
    if (formData.checkInDate && formData.checkOutDate) {
      console.log('Dates changed, loading available rooms...');
      loadAvailableRooms();
    }
  }, [formData.checkInDate, formData.checkOutDate]);

  const loadBookings = async () => {
    setLoading(true);
    console.log('Loading bookings...');
    
    try {
      const params = {};
      if (searchQuery) params.search = searchQuery;
      if (filters.status) params.status = filters.status;
      if (filters.dateRange) params.date_range = filters.dateRange;

      const data = await api.getBookings(params);
      console.log('Bookings loaded:', data);
      
      if (data.success) {
        setBookings(data.bookings || []);
      } else {
        showSnackbar(data.error || 'Failed to load bookings', 'error');
      }
    } catch (error) {
      console.error('Error loading bookings:', error);
      showSnackbar('Failed to load bookings. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableRooms = async () => {
    try {
      setLoadingRooms(true);
      
      // Validate dates
      if (!formData.checkInDate || !formData.checkOutDate) {
        console.log('Missing dates for room availability check');
        showSnackbar('Please select both check-in and check-out dates', 'error');
        return;
      }

      const checkIn = new Date(formData.checkInDate);
      const checkOut = new Date(formData.checkOutDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // Validate date logic
      if (checkIn < today) {
        showSnackbar('Check-in date cannot be in the past', 'error');
        return;
      }

      if (checkOut <= checkIn) {
        showSnackbar('Check-out date must be after check-in date', 'error');
        return;
      }

      console.log('Loading rooms for:', {
        checkIn: formData.checkInDate,
        checkOut: formData.checkOutDate,
        formattedCheckIn: formatDateForAPI(formData.checkInDate),
        formattedCheckOut: formatDateForAPI(formData.checkOutDate),
        hotelCode: getHotelCode()
      });
      
      const data = await api.getRoomAvailability(formData.checkInDate, formData.checkOutDate);
      
      console.log('API Response:', data);
      
      if (data.success) {
        const availableRooms = data.availability || [];
        console.log(`Loaded ${availableRooms.length} rooms`);
        console.log('Available rooms:', availableRooms.filter(room => room.is_available));
        
        setAvailableRooms(availableRooms);
        
        if (availableRooms.length === 0) {
          showSnackbar('No rooms available for selected dates', 'info');
        }
      } else {
        showSnackbar(data.error || 'Failed to load available rooms', 'error');
        setAvailableRooms([]);
      }
    } catch (error) {
      console.error('Error loading available rooms:', error);
      console.error('Error details:', error.response?.data);
      showSnackbar('Failed to load available rooms. Please try again.', 'error');
      setAvailableRooms([]);
    } finally {
      setLoadingRooms(false);
    }
  };

  const countries = [
    'United States', 'United Kingdom', 'Canada', 'Australia', 'Germany', 'France', 'Japan', 'China',
    'India', 'Brazil', 'Mexico', 'Italy', 'Spain', 'South Korea', 'United Arab Emirates', 'Nigeria',
    'South Africa', 'Egypt', 'Kenya', 'Ghana', 'Other'
  ];

  const steps = [
    'Guest Information',
    'Room Selection',
    'Review & Payment'
  ];

  const tabLabels = [
    { label: 'New Booking', icon: <PersonAdd /> },
    { label: 'Bookings', icon: <Search /> },
    { label: 'Rooms', icon: <Hotel /> },
  ];

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleInputChange = (field) => (event) => {
    const value = event.target.value;
    setFormData({
      ...formData,
      [field]: value
    });

    if (errors[field]) {
      setErrors({
        ...errors,
        [field]: ''
      });
    }
  };

  const handleFileUpload = (fileType) => (event) => {
    const files = Array.from(event.target.files);
    
    if (files.length > 0) {
      setUploadedFiles(prev => ({
        ...prev,
        [fileType]: files[0]
      }));
    }
  };

  const removeFile = (fileType) => {
    setUploadedFiles(prev => ({
      ...prev,
      [fileType]: null
    }));
  };

  const validateStep = (step) => {
    const newErrors = {};

    switch (step) {
      case 0:
        if (!formData.firstName) newErrors.firstName = 'First name is required';
        if (!formData.lastName) newErrors.lastName = 'Last name is required';
        if (!formData.email) newErrors.email = 'Email is required';
        if (!formData.phone) newErrors.phone = 'Phone number is required';
        if (!formData.checkInDate) newErrors.checkInDate = 'Check-in date is required';
        if (!formData.checkOutDate) newErrors.checkOutDate = 'Check-out date is required';
        break;
      
      case 1:
        if (!selectedRoom) newErrors.room = 'Please select a room';
        break;
      
      case 2:
        if (!formData.paymentMethod) newErrors.paymentMethod = 'Payment method is required';
        break;
      
      default:
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleSubmit = async () => {
    if (validateStep(activeStep)) {
        setSubmitting(true);
        try {
            // Calculate amounts
            const nights = calculateNights();
            const originalAmount = selectedRoom ? selectedRoom.price * nights : 0;
            
            // Get effective discount percentage (manual or global)
            const effectiveDiscountPercent = getEffectiveDiscountPercentage();
            const isManual = isManualDiscountApplied();
            
            // Calculate discount amount based on effective discount
            const discountAmount = (originalAmount * effectiveDiscountPercent) / 100;
            const totalAmount = originalAmount - discountAmount;

            console.log('=== Booking Submission Debug ===');
            console.log('Original amount:', originalAmount);
            console.log('Effective discount %:', effectiveDiscountPercent);
            console.log('Discount amount:', discountAmount);
            console.log('Total amount:', totalAmount);
            console.log('Is manual discount:', isManual);
            console.log('Manual discount reason:', manualDiscountReason);

            // Prepare booking data for API
            const bookingData = {
                hotel_code: getHotelCode(),
                room_id: selectedRoom.id,
                check_in_date: formatDateForAPI(formData.checkInDate),
                check_out_date: formatDateForAPI(formData.checkOutDate),
                number_of_guests: parseInt(formData.numberOfGuests),
                number_of_rooms: parseInt(formData.numberOfRooms),
                payment_method: formData.paymentMethod,
                special_requests: formData.specialRequests,
                adults: parseInt(formData.numberOfGuests),
                children: 0,
                source: 'walk_in',
                
                // Discount information - CRITICAL for saving to database
                discount_id: activeDiscount && !isManual ? activeDiscount.id : null,
                discount_percentage: effectiveDiscountPercent,
                discount_amount: discountAmount,
                original_amount: originalAmount,
                total_amount: totalAmount,
                discount_reason: isManual 
                    ? manualDiscountReason || 'Manual discount applied by receptionist' 
                    : (activeDiscount ? activeDiscount.notes || 'Global Discount Applied' : null),
                discount_approved_by: isManual 
                    ? getStaffName() 
                    : (activeDiscount ? activeDiscount.set_by : null),
                is_manual_discount: isManual ? 1 : 0,
                
                // Staff information
                staff_id: getStaffCode(),
                staff_name: getStaffName(),
                
                // Guest information
                guest: {
                    title: formData.title,
                    first_name: formData.firstName,
                    last_name: formData.lastName,
                    middle_name: formData.middleName,
                    gender: formData.gender,
                    nationality: formData.nationality,
                    email: formData.email,
                    phone: formData.phone,
                    address: formData.address,
                    country: formData.country,
                    emergency_contact_name: formData.emergencyContactName,
                    emergency_contact_phone: formData.emergencyContactPhone,
                    preferences: formData.preferences || null
                }
            };

            console.log('Submitting booking with discount data:', {
                discount_percentage: effectiveDiscountPercent,
                discount_amount: discountAmount,
                original_amount: originalAmount,
                total_amount: totalAmount,
                discount_id: bookingData.discount_id,
                is_manual_discount: isManual,
                discount_reason: bookingData.discount_reason
            });

            const result = await api.createBooking(bookingData);
            console.log('Booking creation result:', result);
            
            if (result.success) {
                const discountMessage = effectiveDiscountPercent > 0 
                    ? ` ${effectiveDiscountPercent}% discount (₦${discountAmount.toLocaleString()}) applied.` 
                    : '';
                const discountTypeMessage = isManual ? ' Manual discount' : ' Global discount';
                showSnackbar(`Booking created successfully!${discountMessage}${discountTypeMessage}`, 'success');
                setSuccess(true);
                
                // Reset manual discount after successful booking
                setManualDiscountPercentage(0);
                setManualDiscountReason('');
                setShowManualDiscount(false);
                
                setTimeout(() => {
                    setSuccess(false);
                    setActiveTab(1); // Switch to Bookings tab
                    setActiveStep(0); // Reset form
                    resetForm();
                    loadBookings(); // Refresh bookings list
                    checkActiveDiscount(); // Re-check discount status
                }, 2000);
            } else {
                throw new Error(result.error || 'Failed to create booking');
            }
        } catch (error) {
            console.error('Booking failed:', error);
            const errorMessage = error.response?.data?.error || error.message || 'Booking failed. Please try again.';
            showSnackbar(errorMessage, 'error');
        } finally {
            setSubmitting(false);
        }
    }
  };

  const resetForm = () => {
    setFormData({
      title: '', firstName: '', lastName: '', middleName: '',
      gender: '', nationality: '', email: '', phone: '', 
      address: '', country: '', emergencyContactName: '', 
      emergencyContactPhone: '', preferences: '',
      checkInDate: '', checkOutDate: '', numberOfGuests: 1, 
      numberOfRooms: 1, specialRequests: '', paymentMethod: '', 
      cardNumber: '', expiryDate: '', cvv: '', billingAddress: '',
    });
    setSelectedRoom(null);
    setSelectedExistingGuest(null);
    setFoundGuests([]);
    setManualDiscountPercentage(0);
    setManualDiscountReason('');
    setShowManualDiscount(false);
    setUploadedFiles({
      passportPhoto: null, idDocument: null, paymentReceipt: null,
    });
    // Re-check discount when resetting
    checkActiveDiscount();
  };

  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleFilterChange = (filter) => (event) => {
    setFilters(prev => ({
      ...prev,
      [filter]: event.target.value
    }));
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleStatusUpdate = async (bookingId, newStatus, cancellationReason = null) => {
    try {
      console.log(`Updating booking ${bookingId} to ${newStatus}`);
      const result = await api.updateBookingStatus(bookingId, newStatus, cancellationReason);
      console.log('Status update result:', result);
      
      if (result.success) {
        showSnackbar(`Booking ${newStatus.replace('_', ' ')} successfully!`, 'success');
        loadBookings(); // Refresh the list
      } else {
        showSnackbar(result.error || 'Failed to update booking status', 'error');
      }
    } catch (error) {
      console.error('Error updating booking status:', error);
      showSnackbar('Failed to update booking status. Please try again.', 'error');
    }
  };

  const handleCheckIn = (bookingId) => {
    handleStatusUpdate(bookingId, 'checked_in');
  };

  const handleCheckOut = (bookingId) => {
    handleStatusUpdate(bookingId, 'checked_out');
  };

  const handleCancelBooking = (bookingId) => {
    const reason = prompt('Please enter cancellation reason:');
    if (reason) {
      handleStatusUpdate(bookingId, 'cancelled', reason);
    }
  };

  const filteredBookings = bookings.filter(booking => 
    (booking.guest_name && booking.guest_name.toLowerCase().includes(searchQuery.toLowerCase())) ||
    (booking.booking_number && booking.booking_number.toLowerCase().includes(searchQuery.toLowerCase())) ||
    (booking.room_type && booking.room_type.toLowerCase().includes(searchQuery.toLowerCase()))
  ).filter(booking => 
    (filters.status ? booking.booking_status === filters.status : true)
  );

  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userProfile');
    navigate('/Entri');
  };

  const handleNotificationClick = () => {
    alert('You have new notifications!');
  };

  const showSnackbar = (message, severity) => {
    console.log(`Snackbar: [${severity}] ${message}`);
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleRefresh = () => {
    console.log('Refreshing data...');
    if (activeTab === 1) {
      loadBookings();
    }
    checkActiveDiscount(); // Also refresh discount status
    showSnackbar('Data refreshed successfully!', 'success');
  };

  // Guest Selection Dialog
  const renderGuestSelectionDialog = () => (
    <Dialog 
      open={guestSearchDialog} 
      onClose={() => setGuestSearchDialog(false)}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Person color="primary" />
          <Typography variant="h6">Select Guest</Typography>
        </Box>
        <Typography variant="body2" color="textSecondary">
          Multiple guests found. Please select the correct one:
        </Typography>
      </DialogTitle>
      <DialogContent>
        <List>
          {foundGuests.map((guest) => (
            <ListItem 
              key={guest.id}
              button
              onClick={() => handleManualGuestSelect(guest)}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                mb: 1,
                '&:hover': {
                  backgroundColor: 'action.hover',
                  borderColor: 'primary.main',
                }
              }}
            >
              <ListItemText
                primary={`${guest.title || ''} ${guest.first_name} ${guest.last_name}`.trim()}
                secondary={
                  <Box>
                    <Typography variant="body2">
                      Email: {guest.email} | Phone: {guest.phone}
                    </Typography>
                    <Typography variant="body2">
                      {guest.nationality && `Nationality: ${guest.nationality}`}
                      {guest.address && ` | Address: ${guest.address}`}
                    </Typography>
                    {guest.total_stays > 0 && (
                      <Chip 
                        label={`${guest.total_stays} previous stays`}
                        size="small"
                        color="info"
                        sx={{ mt: 0.5 }}
                      />
                    )}
                  </Box>
                }
              />
            </ListItem>
          ))}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setGuestSearchDialog(false)}>
          Cancel
        </Button>
        <Button 
          onClick={handleClearGuestSelection}
          color="secondary"
        >
          Create New Guest
        </Button>
      </DialogActions>
    </Dialog>
  );

  // View Booking Dialog
  const renderViewBookingDialog = () => (
    <Dialog 
      open={viewBookingDialog} 
      onClose={() => setViewBookingDialog(false)}
      maxWidth="lg"
      fullWidth
      scroll="paper"
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Visibility color="primary" />
            <Typography variant="h6">Booking Details</Typography>
          </Box>
          <IconButton onClick={() => setViewBookingDialog(false)}>
            <Close />
          </IconButton>
        </Box>
        {selectedBooking && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1, flexWrap: 'wrap' }}>
            <StatusChip 
              label={selectedBooking.booking_status} 
              status={selectedBooking.booking_status}
            />
            <Typography variant="body2" color="textSecondary">
              Booking #: {selectedBooking.booking_number}
            </Typography>
            {selectedBooking.discount_percentage > 0 && (
              <Chip 
                icon={<Percent />}
                label={`${selectedBooking.discount_percentage}% OFF`}
                color="success"
                size="small"
              />
            )}
            {selectedBooking.is_manual_discount && (
              <Chip 
                label="Manual Discount"
                color="warning"
                size="small"
                variant="outlined"
              />
            )}
          </Box>
        )}
      </DialogTitle>
      <DialogContent dividers>
        {selectedBooking && (
          <Grid container spacing={3}>
            {/* Guest Information */}
            <Grid item xs={12} md={6}>
              <SectionCard>
                <SectionHeader
                  title="Guest Information"
                  avatar={<Person />}
                />
                <CardContent>
                  <List dense>
                    <ListItem>
                      <ListItemText 
                        primary="Full Name"
                        secondary={`${selectedBooking.title || ''} ${selectedBooking.first_name} ${selectedBooking.last_name}`.trim()}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Contact Information"
                        secondary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                            <Email fontSize="small" />
                            <Typography variant="body2">{selectedBooking.email}</Typography>
                            <Phone fontSize="small" sx={{ ml: 1 }} />
                            <Typography variant="body2">{selectedBooking.phone}</Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                    {selectedBooking.nationality && (
                      <ListItem>
                        <ListItemText 
                          primary="Nationality"
                          secondary={selectedBooking.nationality}
                        />
                      </ListItem>
                    )}
                    {selectedBooking.address && (
                      <ListItem>
                        <ListItemText 
                          primary="Address"
                          secondary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <LocationOn fontSize="small" />
                              {selectedBooking.address}
                            </Box>
                          }
                        />
                      </ListItem>
                    )}
                  </List>
                </CardContent>
              </SectionCard>
            </Grid>

            {/* Stay Information */}
            <Grid item xs={12} md={6}>
              <SectionCard>
                <SectionHeader
                  title="Stay Details"
                  avatar={<CalendarToday />}
                />
                <CardContent>
                  <List dense>
                    <ListItem>
                      <ListItemText 
                        primary="Check-in Date"
                        secondary={selectedBooking.check_in_date}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Check-out Date"
                        secondary={selectedBooking.check_out_date}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Duration"
                        secondary={`${selectedBooking.nights || 0} nights`}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Guests"
                        secondary={`${selectedBooking.adults || 1} adult(s), ${selectedBooking.children || 0} child(ren)`}
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </SectionCard>
            </Grid>

            {/* Room Information */}
            <Grid item xs={12} md={6}>
              <SectionCard>
                <SectionHeader
                  title="Room Information"
                  avatar={<Room />}
                />
                <CardContent>
                  <List dense>
                    <ListItem>
                      <ListItemText 
                        primary="Room Type"
                        secondary={selectedBooking.room_name}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Room Number"
                        secondary={selectedBooking.room_number}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Room Category"
                        secondary={selectedBooking.room_type}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Price per Night"
                        secondary={`₦${selectedBooking.room_price}`}
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </SectionCard>
            </Grid>

            {/* Payment Information with Discount */}
            <Grid item xs={12} md={6}>
              <SectionCard>
                <SectionHeader
                  title="Payment Information"
                  avatar={<CreditCard />}
                />
                <CardContent>
                  <List dense>
                    <ListItem>
                      <ListItemText 
                        primary="Original Amount"
                        secondary={`₦${selectedBooking.original_amount || selectedBooking.total_amount}`}
                      />
                    </ListItem>
                    {selectedBooking.discount_percentage > 0 && (
                      <>
                        <ListItem>
                          <ListItemText 
                            primary={`Discount (${selectedBooking.discount_percentage}%)`}
                            secondary={
                              <Box>
                                <Typography component="span" color="success.main" fontWeight="bold">
                                  -₦{selectedBooking.discount_amount || 0}
                                </Typography>
                                {selectedBooking.is_manual_discount && (
                                  <Chip 
                                    label="Manual" 
                                    size="small" 
                                    color="warning"
                                    sx={{ ml: 1 }}
                                  />
                                )}
                              </Box>
                            }
                          />
                        </ListItem>
                        <Divider />
                      </>
                    )}
                    <ListItem>
                      <ListItemText 
                        primary="Total Amount"
                        primaryTypographyProps={{ variant: 'h6', fontWeight: 'bold' }}
                        secondary={`₦${selectedBooking.total_amount}`}
                        secondaryTypographyProps={{ variant: 'h6', fontWeight: 'bold', color: 'primary.main' }}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Paid Amount"
                        secondary={`₦${selectedBooking.paid_amount || 0}`}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Payment Status"
                        secondary={
                          <StatusChip 
                            label={selectedBooking.payment_status} 
                            status={selectedBooking.payment_status}
                            size="small"
                          />
                        }
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Payment Method"
                        secondary={selectedBooking.payment_method || 'Not specified'}
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </SectionCard>
            </Grid>

            {/* Special Requests */}
            {selectedBooking.special_requests && (
              <Grid item xs={12}>
                <SectionCard>
                  <SectionHeader
                    title="Special Requests"
                    avatar={<Edit />}
                  />
                  <CardContent>
                    <Typography variant="body2">
                      {selectedBooking.special_requests}
                    </Typography>
                  </CardContent>
                </SectionCard>
              </Grid>
            )}
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setViewBookingDialog(false)}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderNewBookingTab = () => (
    <Paper elevation={3} sx={{ p: 4, mt: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4" color="primary">
          Hotel Guest Booking
        </Typography>
        {/* Active Discount Banner */}
        {discountLoading && <CircularProgress size={24} />}
        {!discountLoading && activeDiscount && (
          <Chip
            icon={<Percent />}
            label={`${activeDiscount.discount_percentage}% OFF ACTIVE!`}
            color="success"
            size="large"
            sx={{ 
              fontWeight: 'bold', 
              fontSize: '1rem',
              py: 2,
              animation: 'pulse 2s infinite',
              '@keyframes pulse': {
                '0%': { transform: 'scale(1)' },
                '50%': { transform: 'scale(1.05)' },
                '100%': { transform: 'scale(1)' },
              }
            }}
          />
        )}
      </Box>
     
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Booking confirmed successfully! Redirecting to bookings...
        </Alert>
      )}

      <Stepper activeStep={activeStep} sx={{ mb: 4, mt: 2 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Box sx={{ mb: 4 }}>
        {renderStepContent(activeStep)}
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Button
          onClick={handleBack}
          disabled={activeStep === 0}
          variant="outlined"
        >
          Back
        </Button>

        {activeStep === steps.length - 1 ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
            <Box sx={{ textAlign: 'right' }}>
              {getEffectiveDiscountPercentage() > 0 && calculateDiscountAmount() > 0 && (
                <>
                  <Typography variant="body2" color="textSecondary" sx={{ textDecoration: 'line-through' }}>
                    Original: ₦{calculateOriginalTotal().toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="success.main">
                    {isManualDiscountApplied() ? 'Manual ' : ''}Discount ({getEffectiveDiscountPercentage()}%): -₦{calculateDiscountAmount().toLocaleString()}
                  </Typography>
                  {isManualDiscountApplied() && (
                    <Typography variant="caption" color="textSecondary" display="block">
                      Reason: {manualDiscountReason || 'Not specified'}
                    </Typography>
                  )}
                </>
              )}
              <Typography variant="h6" color="primary">
                Total: ₦{calculateTotal().toLocaleString()}
              </Typography>
            </Box>
            <Button
              onClick={handleSubmit}
              variant="contained"
              color="primary"
              size="large"
              startIcon={submitting ? <CircularProgress size={20} /> : <CheckCircle />}
              disabled={submitting}
            >
              {submitting ? 'Creating...' : 'Confirm Booking'}
            </Button>
          </Box>
        ) : (
          <Button
            onClick={handleNext}
            variant="contained"
            color="primary"
          >
            Next
          </Button>
        )}
      </Box>
    </Paper>
  );

  const renderBookingsTab = () => (
    <Box sx={{ mt: 2 }}>
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            placeholder="Search by guest name, booking number, or room type..."
            value={searchQuery}
            onChange={handleSearch}
            sx={{ flexGrow: 1, minWidth: 300 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search color="action" />
                </InputAdornment>
              ),
            }}
          />
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={filters.status}
              onChange={handleFilterChange('status')}
              label="Status"
            >
              <MenuItem value="">All Status</MenuItem>
              <MenuItem value="confirmed">Confirmed</MenuItem>
              <MenuItem value="checked_in">Checked In</MenuItem>
              <MenuItem value="checked_out">Checked Out</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
            </Select>
          </FormControl>
          <Button variant="outlined" startIcon={<Refresh />} onClick={handleRefresh} disabled={loading}>
            Refresh
          </Button>
          <Button variant="contained" startIcon={<Download />}>
            Export
          </Button>
        </Box>
      </Paper>

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && filteredBookings.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="textSecondary">
            No bookings found
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            {searchQuery || filters.status ? 'Try adjusting your search or filters' : 'No bookings have been made yet'}
          </Typography>
        </Paper>
      )}

      {!loading && filteredBookings.length > 0 && (
        <TableContainer component={Paper} elevation={3}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'primary.main' }}>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Guest Name</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Room Type</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Room No</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Booking No</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Check-in</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Check-out</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Paid</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Discount</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredBookings
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((booking) => (
                  <TableRow key={booking.id} hover>
                    <TableCell>
                      <Typography fontWeight="medium">{booking.guest_name}</Typography>
                      <Typography variant="body2" color="textSecondary">
                        {booking.email}
                      </Typography>
                    </TableCell>
                    <TableCell>{booking.room_type}</TableCell>
                    <TableCell>
                      <Chip 
                        label={booking.room_number} 
                        variant="outlined" 
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={booking.booking_number} 
                        variant="outlined" 
                        size="small"
                        color="primary"
                      />
                    </TableCell>
                    <TableCell>
                      <StatusChip 
                        label={booking.booking_status} 
                        status={booking.booking_status}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{booking.check_in_date}</TableCell>
                    <TableCell>{booking.check_out_date}</TableCell>
                    <TableCell>
                      <Typography fontWeight="bold" color="primary">
                        ₦{booking.total_amount}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {booking.discount_percentage > 0 ? (
                        <Box>
                          <Chip 
                            label={`${booking.discount_percentage}% OFF`}
                            color="success"
                            size="small"
                          />
                          {booking.is_manual_discount && (
                            <Chip 
                              label="Manual"
                              size="small"
                              color="warning"
                              variant="outlined"
                              sx={{ ml: 0.5 }}
                            />
                          )}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="textSecondary">-</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <IconButton 
                          size="small" 
                          color="primary"
                          onClick={() => handleViewBooking(booking)}
                        >
                          <Visibility />
                        </IconButton>
                        {booking.booking_status === 'confirmed' && (
                          <Button
                            size="small"
                            variant="outlined"
                            color="success"
                            onClick={() => handleCheckIn(booking.id)}
                          >
                            Check In
                          </Button>
                        )}
                        {booking.booking_status === 'checked_in' && (
                          <Button
                            size="small"
                            variant="outlined"
                            color="info"
                            onClick={() => handleCheckOut(booking.id)}
                          >
                            Check Out
                          </Button>
                        )}
                        {(booking.booking_status === 'confirmed' || booking.booking_status === 'pending') && (
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            onClick={() => handleCancelBooking(booking.id)}
                          >
                            Cancel
                          </Button>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredBookings.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
      )}
    </Box>
  );

  const renderRoomsTab = () => (
    <Box sx={{ mt: 2 }}>
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Available Rooms
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Check-in Date"
              type="date"
              value={formData.checkInDate}
              onChange={handleInputChange('checkInDate')}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Check-out Date"
              type="date"
              value={formData.checkOutDate}
              onChange={handleInputChange('checkOutDate')}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Button 
              variant="contained" 
              onClick={loadAvailableRooms}
              disabled={!formData.checkInDate || !formData.checkOutDate}
              sx={{ mt: 1 }}
            >
              Check Availability
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={3}>
        {availableRooms
          .filter(room => room.is_available === "1" || room.is_available === 1 || room.is_available === true)
          .map((room) => (
          <Grid item xs={12} md={6} key={room.id}>
            <RoomCard
              className={selectedRoom?.id === room.id ? 'selected' : ''}
              onClick={() => setSelectedRoom(room)}
            >
              <CardContent>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Box
                    sx={{
                      width: 120,
                      height: 100,
                      backgroundColor: 'grey.200',
                      borderRadius: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Room sx={{ fontSize: 40, color: 'grey.400' }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Typography variant="h6" gutterBottom>
                        {room.name}
                      </Typography>
                      <Typography variant="h6" color="primary">
                        ₦{room.price}/night
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                      {room.type}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <Typography variant="body2">Capacity:</Typography>
                      <Chip label={`${room.capacity} guests`} size="small" />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip 
                        label={room.is_available ? 'Available' : 'Not Available'} 
                        color={room.is_available ? 'success' : 'error'}
                        size="small"
                      />
                      <Rating value={parseFloat(room.rating) || 0} precision={0.5} readOnly size="small" />
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </RoomCard>
          </Grid>
        ))}
      </Grid>

      {availableRooms.length === 0 && formData.checkInDate && formData.checkOutDate && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="textSecondary">
            No available rooms for selected dates
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            Please try different dates or contact reception for assistance.
          </Typography>
        </Paper>
      )}
    </Box>
  );

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box>
            {/* Guest Information Section */}
            <SectionCard>
              <SectionHeader
                title="Guest Information"
                avatar={<Person />}
              />
              <CardContent>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      {selectedExistingGuest && (
                        <Chip 
                          icon={<History />}
                          label={`Existing Guest: ${selectedExistingGuest.first_name} ${selectedExistingGuest.last_name}`}
                          color="success"
                          onDelete={handleClearGuestSelection}
                        />
                      )}
                    </Box>
                    {foundGuests.length > 1 && (
                      <Alert severity="info" sx={{ mb: 2, mt: 1 }}>
                        Multiple guests found. Please select the correct one from the list.
                      </Alert>
                    )}
                  </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Email *"
                    type="email"
                    value={formData.email}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFormData({ ...formData, email: value });
                      
                      // Email validation
                      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                      if (value && !emailRegex.test(value)) {
                        setErrors({ ...errors, email: 'Please enter a valid email address' });
                      } else {
                        setErrors({ ...errors, email: '' });
                      }
                    }}
                    error={!!errors.email}
                    helperText={errors.email || (searchingGuest ? 'Searching for existing guests...' : 'Enter a valid email address (e.g., name@example.com)')}
                    InputProps={{
                      endAdornment: searchingGuest ? (
                        <InputAdornment position="end">
                          <CircularProgress size={20} />
                        </InputAdornment>
                      ) : formData.email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email) ? (
                        <InputAdornment position="end">
                          <CheckCircle color="success" fontSize="small" />
                        </InputAdornment>
                      ) : null,
                    }}
                  />
                </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Phone *"
                      value={formData.phone}
                      onChange={handleInputChange('phone')}
                      error={!!errors.phone}
                      helperText={errors.phone}
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth sx={{ minWidth: 120 }}>
                      <InputLabel>Title</InputLabel>
                      <Select
                        value={formData.title}
                        onChange={handleInputChange('title')}
                        label="Title"
                      >
                        <MenuItem value="Mr">Mr</MenuItem>
                        <MenuItem value="Mrs">Mrs</MenuItem>
                        <MenuItem value="Miss">Miss</MenuItem>
                        <MenuItem value="Ms">Ms</MenuItem>
                        <MenuItem value="Dr">Dr</MenuItem>
                        <MenuItem value="Prof">Prof</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="First Name *"
                      value={formData.firstName}
                      onChange={handleInputChange('firstName')}
                      error={!!errors.firstName}
                      helperText={errors.firstName}
                    />
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="Last Name *"
                      value={formData.lastName}
                      onChange={handleInputChange('lastName')}
                      error={!!errors.lastName}
                      helperText={errors.lastName}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth sx={{ minWidth: 120 }}>
                      <InputLabel>Gender</InputLabel>
                      <Select
                        value={formData.gender}
                        onChange={handleInputChange('gender')}
                        label="Gender"
                      >
                        <MenuItem value="Male">Male</MenuItem>
                        <MenuItem value="Female">Female</MenuItem>
                        <MenuItem value="Other">Other</MenuItem>
                        <MenuItem value="Prefer not to say">Prefer not to say</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth sx={{ minWidth: 120 }}>
                      <InputLabel>Nationality</InputLabel>
                      <Select
                        value={formData.nationality}
                        onChange={handleInputChange('nationality')}
                        label="Nationality"
                      >
                        {countries.map(country => (
                          <MenuItem key={country} value={country}>{country}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Address"
                      value={formData.address}
                      onChange={handleInputChange('address')}
                      placeholder="Street address"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControl fullWidth sx={{ minWidth: 120 }}>
                      <InputLabel>Country</InputLabel>
                      <Select
                        value={formData.country}
                        onChange={handleInputChange('country')}
                        label="Country"
                      >
                        {countries.map(country => (
                          <MenuItem key={country} value={country}>{country}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Emergency Contact Name"
                      value={formData.emergencyContactName}
                      onChange={handleInputChange('emergencyContactName')}
                      placeholder="Full name of emergency contact"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Emergency Contact Phone"
                      value={formData.emergencyContactPhone}
                      onChange={handleInputChange('emergencyContactPhone')}
                      placeholder="Phone number of emergency contact"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </SectionCard>

            {/* Stay Details Section */}
            <SectionCard>
              <SectionHeader
                title="Stay Details"
                avatar={<CalendarToday />}
              />
              <CardContent>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Check-in Date *"
                      type="date"
                      value={formData.checkInDate}
                      onChange={handleInputChange('checkInDate')}
                      InputLabelProps={{ shrink: true }}
                      error={!!errors.checkInDate}
                      helperText={errors.checkInDate}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Check-out Date *"
                      type="date"
                      value={formData.checkOutDate}
                      onChange={handleInputChange('checkOutDate')}
                      InputLabelProps={{ shrink: true }}
                      error={!!errors.checkOutDate}
                      helperText={errors.checkOutDate}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth sx={{ minWidth: 150 }}
                      label="Number of Guests"
                      type="number"
                      value={formData.numberOfGuests}
                      onChange={handleInputChange('numberOfGuests')}
                      InputProps={{ inputProps: { min: 1, max: 10 } }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth sx={{ minWidth: 150 }}
                      label="Number of Rooms"
                      type="number"
                      value={formData.numberOfRooms}
                      onChange={handleInputChange('numberOfRooms')}
                      InputProps={{ inputProps: { min: 1, max: 5 } }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Special Requests"
                      multiline
                      rows={3}
                      value={formData.specialRequests}
                      onChange={handleInputChange('specialRequests')}
                      placeholder="Any special requirements or requests..."
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </SectionCard>
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom color="primary" sx={{ mb: 3 }}>
              Select Your Room
            </Typography>
            
            {!formData.checkInDate || !formData.checkOutDate ? (
              <Alert severity="info" sx={{ mb: 3 }}>
                Please select check-in and check-out dates first to see available rooms.
              </Alert>
            ) : loadingRooms ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>Loading available rooms...</Typography>
              </Box>
            ) : availableRooms.filter(room => room.is_available === "1" || room.is_available === 1 || room.is_available === true).length === 0 ? (
              <Alert severity="warning" sx={{ mb: 3 }}>
                No rooms available for the selected dates. Please try different dates.
              </Alert>
            ) : (
              <Grid container spacing={3}>
                {availableRooms
                  .filter(room => room.is_available === "1" || room.is_available === 1 || room.is_available === true)
                  .map((room) => (
                  <Grid item xs={12} key={room.id}>
                    <RoomCard
                      className={selectedRoom?.id === room.id ? 'selected' : ''}
                      onClick={() => setSelectedRoom(room)}
                    >
                      <CardContent>
                        <Grid container spacing={3}>
                          <Grid item xs={12} md={3}>
                            <Box
                              sx={{
                                width: '100%',
                                height: 140,
                                backgroundColor: 'grey.200',
                                borderRadius: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <Room sx={{ fontSize: 60, color: 'grey.400' }} />
                            </Box>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                              <Typography variant="h6">{room.name}</Typography>
                              <Typography variant="h5" color="primary" fontWeight="bold">
                                ₦{room.price}
                                <Typography variant="body2" color="textSecondary" component="span">
                                  /night
                                </Typography>
                              </Typography>
                            </Box>
                            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                              {room.type} • Room {room.number}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                              <Chip 
                                icon={<Person />} 
                                label={`${room.capacity} guests`} 
                                size="small" 
                                variant="outlined" 
                              />
                              <Rating value={parseFloat(room.rating) || 0} precision={0.5} readOnly size="small" />
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Chip 
                                label="Available" 
                                color="success"
                                size="small"
                              />
                              <Typography variant="body2" color="textSecondary">
                                {room.size} m²
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={12} md={3} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Button
                              variant={selectedRoom?.id === room.id ? "contained" : "outlined"}
                              color="primary"
                              fullWidth
                            >
                              {selectedRoom?.id === room.id ? 'Selected' : 'Select Room'}
                            </Button>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </RoomCard>
                  </Grid>
                ))}
              </Grid>
            )}
            {errors.room && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {errors.room}
              </Alert>
            )}
          </Box>
        );

      case 2:
        const originalTotal = calculateOriginalTotal();
        const discountAmount = calculateDiscountAmount();
        const finalTotal = calculateTotal();
        const effectiveDiscountPercent = getEffectiveDiscountPercentage();
        const isManual = isManualDiscountApplied();
        const discountSource = getDiscountSource();
        const nights = calculateNights();
        
        return (
          <Box>
            <Typography variant="h5" gutterBottom align="center" color="primary" sx={{ mb: 4 }}>
              Review Your Booking
            </Typography>
            
            <Grid container spacing={3}>
              {/* Guest Information */}
              <Grid item xs={12} md={6}>
                <ReviewCard>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Person color="primary" sx={{ mr: 2 }} />
                      <Typography variant="h6" color="primary">
                        Guest Information
                      </Typography>
                    </Box>
                    <List dense>
                      <ListItem>
                        <ListItemText 
                          primary="Full Name" 
                          secondary={`${formData.title} ${formData.firstName} ${formData.middleName} ${formData.lastName}`.trim()}
                        />
                      </ListItem>
                      <Divider component="li" />
                      <ListItem>
                        <ListItemText 
                          primary="Personal Details" 
                          secondary={`${formData.gender || 'Not specified'} | ${formData.nationality || 'Not provided'}`}
                        />
                      </ListItem>
                      <Divider component="li" />
                      <ListItem>
                        <ListItemText 
                          primary="Contact" 
                          secondary={`${formData.email} | ${formData.phone}`}
                        />
                      </ListItem>
                      <Divider component="li" />
                      <ListItem>
                        <ListItemText 
                          primary="Address" 
                          secondary={`${formData.address || 'Not provided'}, ${formData.country || ''}`.trim()}
                        />
                      </ListItem>
                      <Divider component="li" />
                      <ListItem>
                        <ListItemText 
                          primary="Emergency Contact" 
                          secondary={`${formData.emergencyContactName || 'Not provided'} | ${formData.emergencyContactPhone || 'Not provided'}`}
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </ReviewCard>
              </Grid>

              {/* Stay Information */}
              <Grid item xs={12} md={6}>
                <ReviewCard>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <CalendarToday color="primary" sx={{ mr: 2 }} />
                      <Typography variant="h6" color="primary">
                        Stay Details
                      </Typography>
                    </Box>
                    <List dense>
                      <ListItem>
                        <ListItemText 
                          primary="Duration" 
                          secondary={`${formData.checkInDate} to ${formData.checkOutDate} (${nights} nights)`}
                        />
                      </ListItem>
                      <Divider component="li" />
                      <ListItem>
                        <ListItemText 
                          primary="Guests" 
                          secondary={`${formData.numberOfGuests} guest(s), ${formData.numberOfRooms} room(s)`}
                        />
                      </ListItem>
                      <Divider component="li" />
                      <ListItem>
                        <ListItemText 
                          primary="Special Requests" 
                          secondary={formData.specialRequests || 'None'}
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </ReviewCard>
              </Grid>

              {/* Room Information */}
              <Grid item xs={12} md={6}>
                <ReviewCard>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Room color="primary" sx={{ mr: 2 }} />
                      <Typography variant="h6" color="primary">
                        Room Selection
                      </Typography>
                    </Box>
                    {selectedRoom && (
                      <List dense>
                        <ListItem>
                          <ListItemText 
                            primary="Room Type" 
                            secondary={selectedRoom.name}
                          />
                        </ListItem>
                        <Divider component="li" />
                        <ListItem>
                          <ListItemText 
                            primary="Description" 
                            secondary={selectedRoom.type}
                          />
                        </ListItem>
                        <Divider component="li" />
                        <ListItem>
                          <ListItemText 
                            primary="Capacity" 
                            secondary={`${selectedRoom.capacity} guests`}
                          />
                        </ListItem>
                        <Divider component="li" />
                        <ListItem>
                          <ListItemText 
                            primary="Price per Night" 
                            secondary={`₦${selectedRoom.price}`}
                          />
                        </ListItem>
                      </List>
                    )}
                  </CardContent>
                </ReviewCard>
              </Grid>

              {/* Payment Information */}
              <Grid item xs={12}>
                <ReviewCard>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <CreditCard color="primary" sx={{ mr: 2 }} />
                      <Typography variant="h6" color="primary">
                        Payment Method
                      </Typography>
                    </Box>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth sx={{ minWidth: 180 }} error={!!errors.paymentMethod}>
                          <InputLabel>Payment Method *</InputLabel>
                          <Select
                            value={formData.paymentMethod}
                            onChange={handleInputChange('paymentMethod')}
                            label="Payment Method *"
                          >
                            <MenuItem value="Bank Transfer">Bank Transfer</MenuItem>
                            <MenuItem value="Cash">Cash on Arrival</MenuItem>
                            <MenuItem value="POS Terminal">POS Terminal</MenuItem>
                            <MenuItem value="Cash and Transfer">Cash and Transfer</MenuItem>
                            <MenuItem value="Cash and POS">Cash and POS</MenuItem>
                          </Select>
                          {errors.paymentMethod && (
                            <Typography color="error" variant="caption">
                              {errors.paymentMethod}
                            </Typography>
                          )}
                        </FormControl>
                      </Grid>
                    </Grid>
                  </CardContent>
                </ReviewCard>
              </Grid>

              {/* NEW: Manual Discount Section - Add this after Payment Information and before Cost Summary */}
              <Grid item xs={12}>
                <ReviewCard sx={{ borderColor: isManual ? 'success.main' : 'divider' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Percent color={isManual ? 'success' : 'action'} sx={{ mr: 2 }} />
                        <Typography variant="h6" color={isManual ? 'success.main' : 'text.primary'}>
                          Manual Discount
                        </Typography>
                        {isManual && (
                          <Chip 
                            label="Active" 
                            color="success" 
                            size="small" 
                            sx={{ ml: 2 }}
                          />
                        )}
                      </Box>
                      <Button
                        variant={isManual ? "outlined" : "contained"}
                        color={isManual ? "error" : "primary"}
                        size="small"
                        startIcon={isManual ? <RemoveCircle /> : <AddCircle />}
                        onClick={toggleManualDiscount}
                      >
                        {isManual ? 'Remove Discount' : 'Apply Manual Discount'}
                      </Button>
                    </Box>
                    
                    {showManualDiscount && (
                      <Box sx={{ mt: 2 }}>
                        <Alert severity="info" sx={{ mb: 2 }}>
                          Apply a custom discount percentage to this specific booking. This will override any global discount.
                          {activeDiscount && (
                            <Typography variant="body2" sx={{ mt: 1 }}>
                              Note: Global discount of {activeDiscount.discount_percentage}% will be overridden.
                            </Typography>
                          )}
                        </Alert>
                        
                        <Grid container spacing={3}>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              label="Discount Percentage (%)"
                              type="number"
                              value={manualDiscountPercentage}
                              onChange={handleManualDiscountChange}
                              InputProps={{
                                inputProps: { min: 0, max: 100, step: 0.5 },
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <Tooltip title="Enter percentage between 0 and 100">
                                      <Info color="action" fontSize="small" />
                                    </Tooltip>
                                  </InputAdornment>
                                ),
                              }}
                              helperText="Enter a value between 0 and 100"
                              error={manualDiscountPercentage > 100 || manualDiscountPercentage < 0}
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              label="Discount Reason"
                              value={manualDiscountReason}
                              onChange={handleManualDiscountReasonChange}
                              placeholder="e.g., Special promotion, Loyalty customer, etc."
                              helperText="Provide a reason for the manual discount (optional but recommended)"
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
                              <Typography variant="body2" color="success.dark">
                                <strong>Preview:</strong> {manualDiscountPercentage > 0 ? (
                                  `${manualDiscountPercentage}% discount = ₦${((originalTotal * manualDiscountPercentage) / 100).toLocaleString()} off`
                                ) : (
                                  'No discount applied yet'
                                )}
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>
                      </Box>
                    )}
                    
                    {!showManualDiscount && activeDiscount && (
                      <Alert severity="info" sx={{ mt: 1 }}>
                        Global discount of {activeDiscount.discount_percentage}% is currently active.
                        Click "Apply Manual Discount" to override with a custom discount.
                      </Alert>
                    )}
                  </CardContent>
                </ReviewCard>
              </Grid>

              {/* Cost Summary with Discount */}
              <Grid item xs={12}>
                <ReviewCard>
                  <CardContent>
                    <Typography variant="h6" gutterBottom color="primary">
                      Cost Summary
                    </Typography>
                    <List>
                      <ListItem>
                        <ListItemText 
                          primary={`${selectedRoom?.name} (${nights} nights × ₦${selectedRoom?.price})`}
                        />
                        <Typography fontWeight="bold">
                          ₦{originalTotal.toLocaleString()}
                        </Typography>
                      </ListItem>
                      
                      {effectiveDiscountPercent > 0 && discountAmount > 0 && (
                        <>
                          <Divider />
                          <ListItem sx={{ bgcolor: isManual ? '#fff3e0' : '#e8f5e9', borderRadius: 1 }}>
                            <ListItemText 
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                  <Percent color={isManual ? 'warning' : 'success'} />
                                  <Typography color={isManual ? 'warning.main' : 'success.main'} fontWeight="bold">
                                    {isManual ? 'Manual Discount' : 'Global Discount'} ({effectiveDiscountPercent}% OFF)
                                  </Typography>
                                  <Chip 
                                    label={isManual ? `Applied by: ${getStaffName()}` : `Set by: ${activeDiscount?.set_by || 'Admin'}`}
                                    size="small"
                                    variant="outlined"
                                    color={isManual ? 'warning' : 'success'}
                                  />
                                </Box>
                              }
                              secondary={
                                isManual && manualDiscountReason ? 
                                  `Reason: ${manualDiscountReason}` : 
                                  `Set date: ${activeDiscount?.set_date || 'Ongoing'}`
                              }
                            />
                            <Typography color={isManual ? 'warning.main' : 'success.main'} fontWeight="bold">
                              -₦{discountAmount.toLocaleString()}
                            </Typography>
                          </ListItem>
                        </>
                      )}
                      
                      <Divider />
                      <ListItem>
                        <ListItemText 
                          primary="Total Amount" 
                          primaryTypographyProps={{ variant: 'h6', fontWeight: 'bold' }}
                        />
                        <Typography variant="h6" fontWeight="bold" color="primary">
                          ₦{finalTotal.toLocaleString()}
                        </Typography>
                      </ListItem>
                      
                      {effectiveDiscountPercent > 0 && discountAmount > 0 && (
                        <ListItem>
                          <ListItemText 
                            primary="You Save" 
                            primaryTypographyProps={{ variant: 'body2', color: 'success.main' }}
                          />
                          <Typography variant="body2" color="success.main" fontWeight="bold">
                            ₦{discountAmount.toLocaleString()} ({effectiveDiscountPercent}%)
                          </Typography>
                        </ListItem>
                      )}
                      
                      {isManual && (
                        <ListItem>
                          <Alert severity="warning" sx={{ width: '100%' }}>
                            <Typography variant="body2">
                              <strong>Manual Discount Applied:</strong> {manualDiscountPercentage}% discount 
                              {manualDiscountReason && ` for: ${manualDiscountReason}`}
                            </Typography>
                          </Alert>
                        </ListItem>
                      )}
                    </List>
                  </CardContent>
                </ReviewCard>
              </Grid>
            </Grid>
          </Box>
        );

      default:
        return null;
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 0:
        return renderNewBookingTab();
      case 1:
        return renderBookingsTab();
      case 2:
        return renderRoomsTab();
      default:
        return renderNewBookingTab();
    }
  };

  return (
    <Box sx={{ display: 'flex' }}>
      {/* Sidebar */}
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />

      {/* Main Content */}
      <Box
        sx={{
          flexGrow: 1,
          p: 3,
          ml: isMobile ? 0 : sidebarOpen ? 30 : 5,
          mt: 0,
          maxWidth: '1400px',
          mx: 'auto',
        }}
      >
        {/* Top Navigation */}
        <TopNav
          onLogout={handleLogout}
          onNotificationClick={handleNotificationClick}
          toggleSidebar={toggleSidebar}
        />

        {/* Main Content Header */}
        <Paper elevation={2} sx={{ p: 3, mt: 3, backgroundColor: 'primary.main', color: 'white' }}>
          <Typography variant="h4" fontWeight="bold">
            {HOTEL_NAME}
          </Typography>
          <Typography variant="subtitle1">
            Premium Hospitality & Exceptional Service
          </Typography>
        </Paper>

        {/* Tabs Section */}
        <Paper elevation={2} sx={{ mt: 2 }}>
          <StyledTabs
            value={activeTab}
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              '& .MuiTabs-flexContainer': {
                justifyContent: 'center',
              },
            }}
          >
            {tabLabels.map((tab, index) => (
              <StyledTab
                key={index}
                label={tab.label}
                icon={tab.icon}
                iconPosition="start"
              />
            ))}
          </StyledTabs>

          {/* Tab Content */}
          <Box sx={{ p: 3 }}>
            {renderTabContent()}
          </Box>
        </Paper>
      </Box>

      {/* Guest Selection Dialog */}
      {renderGuestSelectionDialog()}

      {/* View Booking Dialog */}
      {renderViewBookingDialog()}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default HotelGuestBooking;